/// <reference types="vite/client" />

import axios, { type AxiosInstance } from 'axios'
import qs from 'qs'

interface AuthStorageShape {
  state?: {
    admin?: {
      accessToken?: string
    }
    user?: {
      accessToken?: string
    }
  }
}

type SessionKind = 'admin' | 'user'

class ApiClient {
  private static instance: ApiClient | null = null
  public client: AxiosInstance
  private accessToken: null | string = null
  private refreshPromise: null | Promise<string> = null

  private constructor() {
    const apiUrl = import.meta?.env?.VITE_API_URL
      ? import.meta.env.VITE_API_URL
      : 'http://localhost:3333'

    this.client = axios.create({
      baseURL: apiUrl,
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
      timeout: 30_000,
      withCredentials: true,
    })

    this.client.interceptors.request.use((config) => {
      if (this.accessToken) {
        config.headers = config.headers ?? {}
        config.headers.Authorization = `Bearer ${this.accessToken}`
      }

      return config
    })

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status as number | undefined
        const originalConfig = error.config as
          | (Record<string, unknown> & {
              _retry?: boolean
              headers?: Record<string, string>
              url?: string
            })
          | undefined
        const requestUrl = String(originalConfig?.url ?? '')

        if (
          status !== 401 ||
          !originalConfig ||
          originalConfig._retry ||
          requestUrl.endsWith('/auth/refresh') ||
          requestUrl.endsWith('/admin/auth/refresh') ||
          requestUrl.endsWith('/auth/login') ||
          requestUrl.endsWith('/admin/auth/login')
        ) {
          return Promise.reject(error)
        }

        originalConfig._retry = true

        try {
          const refreshedAccessToken = await this.refreshAccessToken()
          originalConfig.headers = {
            ...(originalConfig.headers as Record<string, string> | undefined),
            Authorization: `Bearer ${refreshedAccessToken}`,
          }

          return await this.client.request(
            originalConfig as Parameters<AxiosInstance['request']>[0]
          )
        } catch (refreshError) {
          this.clearAuth()
          const location = (globalThis as unknown as { location?: { href: string } }).location
          if (location) {
            location.href = '/login'
          }
          return Promise.reject(refreshError)
        }
      }
    )
  }

  public static getInstance() {
    ApiClient.instance ??= new ApiClient()
    return ApiClient.instance
  }

  public async bootstrapAccessToken(kind: 'admin' | 'auto' | 'user' = 'auto') {
    const endpoints =
      kind === 'admin'
        ? ['/admin/auth/refresh']
        : kind === 'user'
          ? ['/auth/refresh']
          : ['/admin/auth/refresh', '/auth/refresh']

    for (const endpoint of endpoints) {
      try {
        const response = await this.client.post<{ accessToken: string }>(endpoint)
        const token = response.data.accessToken
        this.setAccessToken(token)
        return token
      } catch {
        continue
      }
    }

    this.clearAccessToken()
    return null
  }

  public clearAccessToken() {
    this.accessToken = null
  }

  public getAccessToken() {
    return this.accessToken
  }

  public setAccessToken(token: string) {
    this.accessToken = token
  }

  private clearAuth() {
    const storage = this.getStorage()
    this.clearAccessToken()
    if (!storage) {
      return
    }

    storage.removeItem('auth')
  }

  private getPersistedAuthState() {
    const storage = this.getStorage()

    if (!storage) {
      return null
    }

    const persistedAuth = storage.getItem('auth')

    if (!persistedAuth) {
      return null
    }

    try {
      return JSON.parse(persistedAuth) as AuthStorageShape
    } catch {
      return null
    }
  }

  private getSessionKind(): null | SessionKind {
    const state = this.getPersistedAuthState()?.state
    if (state?.admin) {
      return 'admin'
    }

    if (state?.user) {
      return 'user'
    }

    return null
  }

  private getStorage() {
    return (
      globalThis as unknown as {
        localStorage?: {
          getItem: (key: string) => null | string
          removeItem: (key: string) => void
          setItem: (key: string, value: string) => void
        }
      }
    ).localStorage
  }

  private async refreshAccessToken() {
    if (!this.refreshPromise) {
      this.refreshPromise = this.requestRefreshToken()
    }

    try {
      return await this.refreshPromise
    } finally {
      this.refreshPromise = null
    }
  }

  private async requestRefreshToken() {
    const sessionKind = this.getSessionKind()

    if (!sessionKind) {
      throw new Error('No active session available for refresh')
    }

    const endpoint = sessionKind === 'admin' ? '/admin/auth/refresh' : '/auth/refresh'
    const response = await this.client.post<{ accessToken: string }>(endpoint)
    const accessToken = response.data.accessToken

    this.setAccessToken(accessToken)
    return accessToken
  }
}

export const apiClient = ApiClient.getInstance()

export const bootstrapAccessToken = (kind: 'admin' | 'auto' | 'user' = 'auto') =>
  apiClient.bootstrapAccessToken(kind)

export const clearAccessToken = () => apiClient.clearAccessToken()

export const getAccessToken = () => apiClient.getAccessToken()

export const setAccessToken = (token: string) => apiClient.setAccessToken(token)
